import React, { Dispatch, SetStateAction, useState } from 'react'
import isEmpty from 'lodash-es/isEmpty'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import {
  Box,
  Callout,
  Box as Container,
  DatePicker,
  Radio,
  SingleDate,
} from '@node-space/ui-components'
import type { DateErrorType, DateType } from '@node-space/ui-components'
import { useOnceOffExportContext } from 'hooks/context/useOnceOffExportContext'
import { useDateInput } from 'hooks/forms/useDateInput'
import {
  customDateRadioOptions,
  reportTypeRadioOptions,
} from 'pages/WalletDetails/constants/walletTransactionsExportOptions'
import { ErrorCodes } from 'types/errorCodes'
import {
  ReportTypes,
  type ExportFileFormatType,
  type ReportType,
} from 'types/walletTransactionsExport'

interface OnceOffExportFormType {
  downloadAll: boolean
  setExportFormat: Dispatch<SetStateAction<string>>
}

export const OnceOffExportForm = ({ downloadAll, setExportFormat }: OnceOffExportFormType) => {
  const { t } = useTranslation()
  const dateInput = useDateInput()
  const [dateErrors, setDateErrors] = useState<DateErrorType>({
    day: false,
    month: false,
    year: false,
  })

  const { enableReportingService } = useFeatureFlags()
  const {
    formValues,
    setValue,
    exportCustomDateOptions,
    dateValue,
    setDateValue,
    exportFormatOptions,
    allTransactionsExportError,
    isErrorAllTransactionsExport,
    walletTransactionsExportError,
    isErrorWalletTransactionsExport,
    sendTransactionsReportError,
    isErrorSendTransactionsReport,
  } = useOnceOffExportContext()

  // from & to date values of other specified date range options asides "Custom date range" e.g last 30 days, previous N calendar months etc

  const exportCustomDateRadioOptions = customDateRadioOptions(exportCustomDateOptions)

  const customDateFieldIsSelected =
    formValues.selectedDateOptionName === exportCustomDateOptions?.customDateRange?.name

  const isTransactionReport = formValues?.reportType === ReportTypes.TRANSACTION
  const isBalanceReport = formValues?.reportType === ReportTypes.BALANCE

  const reportTypeOptions = reportTypeRadioOptions()

  const handleBalanceDateChange = (date: DateType) => {
    const dateString = dateInput.setDateInputValue(date)
    const isEmptyDate = Object.values(date).every(field => field.trim() === '')
    setValue('balanceDate', isEmptyDate ? undefined : dateString)
  }

  const allTransactionsErrorList = allTransactionsExportError?.errorList
  const walletTransactionsErrorList = walletTransactionsExportError?.errorList
  const sendTransactionsReportErrorData = sendTransactionsReportError?.data

  const getErrorMessage = () => {
    if (!isEmpty(allTransactionsErrorList)) {
      return allTransactionsErrorList?.[0]?.code === ErrorCodes.REPORT_PROCESSING
        ? t('exports.onceOffExportDuplicateError')
        : allTransactionsErrorList?.[0]?.message
    }
    if (!isEmpty(walletTransactionsErrorList)) {
      return walletTransactionsErrorList?.[0]?.code === ErrorCodes.REPORT_PROCESSING
        ? t('exports.onceOffExportDuplicateError')
        : walletTransactionsErrorList?.[0]?.message
    }
    if (!isEmpty(sendTransactionsReportErrorData) && enableReportingService) {
      if (sendTransactionsReportErrorData?.code === ErrorCodes.REPORT_LEDGER_DUPLICATE_REQUEST) {
        return t('exports.onceOffExportDuplicateError')
      }
      if (
        sendTransactionsReportErrorData?.code ===
        ErrorCodes.REPORT_LEDGER_PDF_TRANSACTIONS_LIMIT_REACHED
      ) {
        return sendTransactionsReportErrorData?.message
      }
      if (sendTransactionsReportErrorData?.code === ErrorCodes.REPORT_LEDGER_WALLET_NOT_FOUND) {
        return t('exports.onceOffExportWalletNotFound')
      }
      if (sendTransactionsReportErrorData?.code === ErrorCodes.UNSUPPORTED_TIME_PERIOD) {
        return t('exports.unsupportedTimePeriod')
      }

      return t('oopsSomethingWentWrong')
    }

    return t('oopsSomethingWentWrong')
  }

  const isError =
    (enableReportingService && isErrorSendTransactionsReport) ||
    isErrorAllTransactionsExport ||
    isErrorWalletTransactionsExport

  return (
    <Box tag="form">
      <Container>
        <Box paddingB={16}>
          <p className="ml-4 text-label text-foreground-prominent mb-2">
            {t('walletsPage.reportType')}
          </p>
          <Radio
            onChange={value => {
              setValue('reportType', value as ReportType)
            }}
            options={reportTypeOptions}
            value={formValues.reportType}
            border={false}
            horizontal
          />
        </Box>
        {isTransactionReport && (
          <Radio
            onChange={value => {
              setValue('selectedDateOptionName', value)
            }}
            options={exportCustomDateRadioOptions}
            value={formValues.selectedDateOptionName}
            border={false}
          />
        )}
        {isTransactionReport && customDateFieldIsSelected && (
          <Box paddingL={12}>
            <DatePicker
              id="date-range-input"
              label={t('walletsPage.exportDateOptions.dateRangeInputLabel')}
              placeholder={t('walletsPage.exportDateOptions.dateRangeInputLabel')}
              initialSelected={dateValue}
              onSelect={dateVal => setDateValue(dateVal)}
            />
          </Box>
        )}
        {isBalanceReport && (
          <Box paddingL={12}>
            <SingleDate
              date={dateInput.getDateFields(formValues.balanceDate || '')}
              labels={{
                ...dateInput.singleDateLabels,
                date: t('walletsPage.balanceDate'),
                errorMessage: dateInput.getDateErrorText(null),
              }}
              errors={dateErrors}
              onChange={handleBalanceDateChange}
              onValidate={(errors: DateErrorType) => setDateErrors(errors)}
            />
          </Box>
        )}
        {!downloadAll && isTransactionReport && (
          <Box paddingT={16}>
            <p className="ml-4 text-label text-foreground-prominent">
              {t('walletsPage.exportFormat')}
            </p>
            <Radio
              border={false}
              horizontal
              onChange={value => {
                setValue('selectedExportFormat', value as ExportFileFormatType)
                setExportFormat(value)
              }}
              options={exportFormatOptions}
              value={formValues?.selectedExportFormat}
            />
          </Box>
        )}

        {isError && (
          <Box paddingL={12} paddingB={12} paddingT={16}>
            <Callout variant="error">{getErrorMessage()}</Callout>
          </Box>
        )}
      </Container>
    </Box>
  )
}
